import data from "../data";
import columns from "./tableColumns";
import { Alert, Button, DatePicker, Modal, Spin, Table, message } from "antd";
import { usePaginationPrzekazy } from "../../../context/PaginationContextPrzekazy";
import { useState } from "react";
import SearchInput from "./search";
import ExpandPrzekazyRender from "./ExpandPrzekazyRender";
import dayjs from "dayjs";
import datetimeParser from "../../../components/datetimeParser";
import axios from "../../../context/axiosConfig";

const PrzekazyWygenerowaneTable = () => {
	const { pagination, setPagination } = usePaginationPrzekazy();
	const [expandedRowKeys, setExpandedRowKeys] = useState([]);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [isDateModalOpen, setIsDateModalOpen] = useState(false);
	const [step, setStep] = useState(1);
	const [selectedDate, setSelectedDate] = useState(null);
	const [isSavingDate, setIsSavingDate] = useState(false);

	const onExpand = (expanded, record) => {
		setExpandedRowKeys((prevExpandedRowKeys) => {
			if (expanded) {
				return [
					...prevExpandedRowKeys,
					record.msr_przekazania_generated_groups_id,
				];
			} else {
				return prevExpandedRowKeys.filter(
					(key) => key !== record.msr_przekazania_generated_groups_id,
				);
			}
		});
	};

	const handleTableChange = (paginationInfo) => {
		const { current, pageSize } = paginationInfo;
		setPagination((prev) => ({
			...prev,
			current,
			pagesize: pageSize,
		}));
	};

	const rows = data();
	const hasGroupsWithoutDate = Array.isArray(rows?.data)
		? rows.data.some((item) => item?.hasMissingForwardDate)
		: false;

	const rowSelection = {
		selectedRowKeys,
		onChange: (keys) => setSelectedRowKeys(keys),
	};

	const openSetDateModal = () => {
		setIsDateModalOpen(true);
		setStep(1);
	};

	const closeSetDateModal = () => {
		if (isSavingDate) {
			return;
		}
		setIsDateModalOpen(false);
		setStep(1);
		setSelectedDate(null);
	};

	const goToConfirmStep = () => {
		if (!selectedDate) {
			message.warning("Wybierz datę przekazania paczki.");
			return;
		}
		setStep(2);
	};

	const submitSetDate = async () => {
		if (!selectedDate || selectedRowKeys.length === 0) {
			return;
		}

		setIsSavingDate(true);
		try {
			await axios.post("/przekazania/group/set-date", {
				groupIds: selectedRowKeys,
				dateForward: Math.floor(
					new Date(selectedDate.format("YYYY-MM-DD")).getTime() /
						1000,
				),
			});
			message.success("Ustawiono datę dla wybranych paczek.");
			setSelectedRowKeys([]);
			setPagination({ ...pagination, reload: !pagination.reload });
			closeSetDateModal();
		} catch (error) {
			message.error(
				error?.response?.data?.message ||
					"Nie udało się ustawić daty dla paczek.",
			);
		} finally {
			setIsSavingDate(false);
		}
	};

	return (
		<>
			{hasGroupsWithoutDate && (
				<div style={{ padding: "0 20px 0 20px" }}>
					<Alert
						type="error"
						showIcon
						message="Wygenerowano paczki XML bez uzupełnionej daty wpływu na rachunek rozliczeniowy. Uzupełnij datę dla paczki."
					/>
				</div>
			)}
			<div
				className="table-header"
				style={{
					display: "flex",
					gap: "10px",
					marginBottom: "16px",
					padding: "20px",
					paddingBottom: "0",
				}}
			>
				<SearchInput />
				<Button
					type="primary"
					disabled={selectedRowKeys.length === 0}
					onClick={openSetDateModal}
				>
					USTAW DATĘ
				</Button>
			</div>
			<div
				className="table-body"
				style={{ padding: "20px", paddingTop: "0" }}
			>
				<Spin spinning={pagination.loading}>
					<Table
						rowSelection={rowSelection}
						columns={columns}
						expandable={{
							expandedRowRender: ExpandPrzekazyRender,
							rowExpandable: (record) =>
								record.przekazy && record.przekazy.length > 0,
						}}
						expandedRowKeys={expandedRowKeys}
						onChange={handleTableChange}
						dataSource={rows.data}
						loading={pagination.loading}
						onExpand={onExpand}
						expandIcon={false}
						pagination={{
							current: pagination.current,
							pageSize: pagination.pagesize,
							total: rows.total,
							showSizeChanger: true,
							pageSizeOptions: ["1", "10", "20", "50", "100"],
						}}
					/>
				</Spin>
			</div>
			<Modal
				open={isDateModalOpen}
				title={
					step === 1
						? "Ustaw datę paczek - krok 1/2"
						: "Ustaw datę paczek - krok 2/2"
				}
				onCancel={closeSetDateModal}
				footer={
					step === 1
						? [
								<Button
									key="cancel"
									onClick={closeSetDateModal}
								>
									Anuluj
								</Button>,
								<Button
									key="next"
									type="primary"
									onClick={goToConfirmStep}
								>
									Dalej
								</Button>,
							]
						: [
								<Button
									key="back"
									onClick={() => setStep(1)}
									disabled={isSavingDate}
								>
									Wstecz
								</Button>,
								<Button
									key="save"
									type="primary"
									loading={isSavingDate}
									onClick={submitSetDate}
								>
									Potwierdź datę
								</Button>,
							]
				}
			>
				{step === 1 ? (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: 12,
						}}
					>
						<div>
							Wybierz datę wpływu na rachunek rozliczeniowy dla
							paczek.
						</div>
						<DatePicker
							style={{ width: "100%" }}
							placeholder="Wybierz datę"
							value={selectedDate}
							onChange={(value) => setSelectedDate(value)}
							allowClear={false}
							disabledDate={(current) =>
								current &&
								current > dayjs().add(7, "days").endOf("day")
							}
						/>
					</div>
				) : (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: 8,
						}}
					>
						<div>
							Potwierdź ustawienie daty dla{" "}
							{selectedRowKeys.length} paczek.
						</div>
						<div>
							Wybrana data:{" "}
							{selectedDate
								? datetimeParser(
										Math.floor(
											new Date(
												selectedDate.format(
													"YYYY-MM-DD",
												),
											).getTime() / 1000,
										),
										"YYYY-MM-DD",
									)
								: "brak"}
						</div>
					</div>
				)}
			</Modal>
		</>
	);
};

export default PrzekazyWygenerowaneTable;
